import type { SuspensionObject } from 'open-modules/zdf/component/suspension/suspension';
import { SuspensionStatusType } from 'open-modules/zdf/component/suspension/suspension';
import { defaultPersonaList } from 'util/testpersonas';

import type { TaskWatchState } from '../moddashboardtypes';
import { FlagSource, JudgementType, ModTaskType } from '../moddashboardtypes';

const demoTimestamp = 1700000000000; // Consistent dates for demos

const demoAuthors = {
    // Is the moderator persona
    a: {
        ...defaultPersonaList[0],
        memberSince: new Date(1000000000000),
        authorKey: defaultPersonaList[0].key,
        authorPhoto: defaultPersonaList[0].photoUrl,
        authorName: defaultPersonaList[0].name,
        authorMemberSince: new Date(1000500000000),
    },
    // Used for awaiting decision variants
    b: {
        ...defaultPersonaList[1],
        memberSince: new Date(1000500000000),
        authorKey: defaultPersonaList[1].key,
        authorPhoto: defaultPersonaList[1].photoUrl,
        authorName: defaultPersonaList[1].name,
        authorMemberSince: new Date(1000500000000),
    },
    // Rejected comments, warned twice
    c: {
        ...defaultPersonaList[2],
        memberSince: new Date(1001000000000),
        authorKey: defaultPersonaList[2].key,
        authorPhoto: defaultPersonaList[2].photoUrl,
        authorName: defaultPersonaList[2].name,
        authorMemberSince: new Date(1000500000000),
    },
    // Rejected comments, warned and blocked
    d: {
        ...defaultPersonaList[3],
        memberSince: new Date(1001500000000),
        authorKey: defaultPersonaList[3].key,
        authorPhoto: defaultPersonaList[3].photoUrl,
        authorName: defaultPersonaList[3].name,
        authorMemberSince: new Date(1000500000000),
    },
    // Only approved comments
    e: {
        ...defaultPersonaList[4],
        memberSince: new Date(1002000000000),
        authorKey: defaultPersonaList[4].key,
        authorPhoto: defaultPersonaList[4].photoUrl,
        authorName: defaultPersonaList[4].name,
        authorMemberSince: new Date(1002000000000),
    },
    // Deleted comments
    f: {
        ...defaultPersonaList[5],
        memberSince: new Date(10025000000000),
        authorKey: defaultPersonaList[5].key,
        authorPhoto: defaultPersonaList[5].photoUrl,
        authorName: defaultPersonaList[5].name,
        authorMemberSince: new Date(1002500000000),
    },
    // Approved comment, warned once before
    g: {
        ...defaultPersonaList[6],
        memberSince: new Date(10030000000000),
        authorKey: defaultPersonaList[6].key,
        authorPhoto: defaultPersonaList[6].photoUrl,
        authorName: defaultPersonaList[6].name,
        authorMemberSince: new Date(10030000000000),
    },
    // Approved comment, warned twice before
    h: {
        ...defaultPersonaList[7],
        memberSince: new Date(10035000000000),
        authorKey: defaultPersonaList[7].key,
        authorPhoto: defaultPersonaList[7].photoUrl,
        authorName: defaultPersonaList[7].name,
        authorMemberSince: new Date(10035000000000),
    },
};

const demoModAuthors = {
    c_t1_awaiting_ai: {
        ...demoAuthors.b,
    },
    c_t1_awaiting_blocklist: {
        ...demoAuthors.b,
    },
    c_t1_awaiting_moderator_note: {
        ...demoAuthors.b,
    },
    c_t1_awaiting_report: {
        ...demoAuthors.b,
    },
    c_t1_awaiting_report_multiple: {
        ...demoAuthors.b,
    },
    c_t1_approved_auto: {
        ...demoAuthors.e,
    },
    c_t1_approved_human: {
        ...demoAuthors.e,
    },
    c_t1_rejected: {
        ...demoAuthors.c,
    },
    c_t1_rejected_first_warning: {
        ...demoAuthors.c,
    },
    c_t1_rejected_second_warning: {
        ...demoAuthors.c,
    },
    c_t1_rejected_blocked_first_warning: {
        ...demoAuthors.d,
    },
    c_t1_rejected_blocked_second_warning: {
        ...demoAuthors.d,
    },
    c_t1_rejected_deleted: {
        ...demoAuthors.d,
    },
    c_t1_awaiting_deleted: {
        ...demoAuthors.b,
    },
    c_t1_approved_first_warning: {
        ...demoAuthors.g,
    },
    c_t1_approved_first_warning_authorH: {
        ...demoAuthors.h,
    },
    c_t1_approved_second_warning_authorH: {
        ...demoAuthors.h,
    },
    c_t1_awaiting_blocked: {
        ...demoAuthors.d,
    },
    // Thread 2
    c_t2_awaiting_ai: {
        ...demoAuthors.b,
    },
    c_t2_approved_auto: {
        ...demoAuthors.c,
    },
    c_t2_approved_human: {
        ...demoAuthors.e,
    },
    c_t2_rejected: {
        ...demoAuthors.c,
    },
};

const modulePublicQuestion = {
    thread1: {
        key: 'thread1',
        from: demoAuthors.a.key,
        question: 'Is godzilla a serious problem?',
        time: demoTimestamp,
        commentCount: 100,
        countAwaitingDecision: 1,
        isHeated: true,
    },
    thread2: {
        key: 'thread2',
        from: demoAuthors.b.key,
        question: "What's the best way to survive a zombie apocalypse?",
        time: demoTimestamp - 100000000,
        commentCount: 42,
    },
    thread3: {
        key: 'thread3',
        from: demoAuthors.c.key,
        question: "If aliens invade, who's negotiating?",
        time: demoTimestamp - 200000000,
        commentCount: 58,
        readOnly: true,
    },
    thread4: {
        key: 'thread4',
        from: demoAuthors.d.key,
        question: 'Is pineapple on pizza actually good?',
        time: demoTimestamp - 2000000000,
        commentCount: 230,
    },
};

const commentsThread1 = {
    // Awaiting decision
    c_t1_awaiting_ai: {
        key: 'c_t1_awaiting_ai',
        from: demoAuthors.b.key,
        time: demoTimestamp - 10000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: "This comment was flagged by AI and is awaiting a moderator's decision.",
        violates: true,
    },
    c_t1_awaiting_blocklist: {
        key: 'c_t1_awaiting_blocklist',
        from: demoAuthors.b.key,
        time: demoTimestamp - 10000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: "This comment contains a bad term from the blocklist and is awaiting a moderator's decision.",
        violates: true,
    },
    c_t1_awaiting_report: {
        key: 'c_t1_awaiting_report',
        from: demoAuthors.b.key,
        time: demoTimestamp - 9000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This comment was reported by another user.',
        violates: true,
    },
    c_t1_awaiting_moderator_note: {
        key: 'c_t1_awaiting_moderator_note',
        from: demoAuthors.b.key,
        time: demoTimestamp - 9000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This comment has moderator notes.',
        violates: true,
    },
    c_t1_awaiting_report_multiple: {
        key: 'c_t1_awaiting_report_multiple',
        from: demoAuthors.b.key,
        time: demoTimestamp - 9000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This comment was reported by multiple users.',
        violates: true,
    },
    // Approved
    c_t1_approved_human: {
        key: 'c_t1_approved_human',
        from: demoAuthors.b.key,
        time: demoTimestamp - 20000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This comment was approved by a human moderator.',
    },
    c_t1_approved_auto: {
        key: 'c_t1_approved_auto',
        from: demoAuthors.c.key,
        time: demoTimestamp - 30000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This comment was automatically approved.',
    },
    c_t1_approved_first_warning: {
        key: 'c_t1_approved_first_warning',
        from: demoAuthors.g.key,
        time: demoTimestamp - 35000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This comment was automatically approved and the user was warned before.',
    },
    c_t1_approved_first_warning_authorH: {
        key: 'c_t1_approved_first_warning_authorH',
        from: demoAuthors.h.key,
        time: demoTimestamp - 37000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This comment was automatically approved and the user was warned before.',
    },
    c_t1_approved_second_warning_authorH: {
        key: 'c_t1_approved_second_warning_authorH',
        from: demoAuthors.h.key,
        time: demoTimestamp - 38000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This comment was automatically approved and the user was warned twice before.',
    },
    // Rejected
    c_t1_rejected: {
        key: 'c_t1_rejected',
        from: demoAuthors.d.key,
        time: demoTimestamp - 40000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This comment was rejected by a human moderator.',
        violates: true,
    },
    // Rejected for blocked persona
    c_t1_rejected_blocked_first_warning: {
        key: 'c_t1_rejected_blocked_first_warning',
        from: demoAuthors.d.key,
        time: demoTimestamp - 40000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This comment was rejected by a human moderator and the user was warned for the first time.',
        violates: true,
    },
    c_t1_rejected_blocked_second_warning: {
        key: 'c_t1_rejected_blocked_second_warning',
        from: demoAuthors.d.key,
        time: demoTimestamp - 40000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This comment was rejected by a human moderator and the user was warned for the second time',
        violates: true,
    },
    // Rejected and warned twice for blockable persona
    c_t1_rejected_first_warning: {
        key: 'c_t1_rejected_first_warning',
        from: demoAuthors.c.key,
        time: demoTimestamp - 40000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This comment was rejected by a human moderator and the user was warned for the first time.',
        violates: true,
    },
    c_t1_rejected_second_warning: {
        key: 'c_t1_rejected_second_warning',
        from: demoAuthors.c.key,
        time: demoTimestamp - 40000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This comment was rejected by a human moderator and the user was warned for the second time',
        violates: true,
    },
    c_t1_awaiting_blocked: {
        key: 'c_t1_awaiting_blocked',
        from: demoAuthors.d.key,
        time: demoTimestamp - 20000000,
        instanceKey: modulePublicQuestion.thread1.key,
        text: 'This commented is from a user who is already blocked.',
        violates: true,
    },
};

const commentsThread2 = {
    // Awaiting decision
    c_t2_awaiting_ai: {
        key: 'c_t2_awaiting_ai',
        from: demoAuthors.b.key,
        time: demoTimestamp - 10000000,
        instanceKey: modulePublicQuestion.thread2.key,
        text: 'These lazy kids form today would not last a minute in the apocalypse.',
        violates: true,
    },
    c_t2_approved_human: {
        key: 'c_t2_approved_human',
        from: demoAuthors.b.key,
        time: demoTimestamp - 20000000,
        instanceKey: modulePublicQuestion.thread2.key,
        text: 'Everyone needs an emergency survival kit',
    },
    c_t2_rejected: {
        key: 'c_t2_rejected',
        from: demoAuthors.d.key,
        time: demoTimestamp - 40000000,
        instanceKey: modulePublicQuestion.thread2.key,
        text: 'This would be very fun.',
        violates: true,
    },
    c_t2_approved_auto: {
        key: 'c_t2_approved_auto',
        from: demoAuthors.c.key,
        time: demoTimestamp - 30000000,
        instanceKey: modulePublicQuestion.thread2.key,
        text: 'I hope we will never experience an apocalypse.',
    },
};

const backlinksThread1 = {
    article0: {
        title: 'Godzilla on a rampage',
        key: 'article0',
        image: 'https://new-public-demo.web.app/photos/godzilla_new_york.jpeg',
    },
    article1: {
        title: 'King Kong attacks Godzilla',
        key: 'article1',
        image: 'https://new-public-demo.web.app/photos/godzilla_new_york.jpeg',
    },
};

const modTasksThread1 = {
    //Awaitng decision
    c_t1_awaiting_ai: {
        ...commentsThread1.c_t1_awaiting_ai,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 10000000,
        objectTime: demoTimestamp - 10000000,
        appealText: 'I think my comment is harmless.',
        violations: '["No violent content or calls for harm"]',
        source: FlagSource.PRE_MODERATION,
        metadata: {
            explanation: 'This message can be interpreted as a call for harm.',
            rewrite: "Here's why I think X, Y, and Z are bad.",
            rulesBroken: '[2]',
        },
    },
    c_t1_awaiting_blocklist: {
        ...commentsThread1.c_t1_awaiting_blocklist,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 10000000,
        objectTime: demoTimestamp - 10000000,
        metadata: {
            blocklistedTerm: 'bad',
        },
        violations: '["Express your ideas with civility"]',
        source: FlagSource.PRE_MODERATION,
    },
    c_t1_awaiting_report: {
        ...commentsThread1.c_t1_awaiting_report,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 5000000,
        objectTime: demoTimestamp - 9000000,
        violations: '["No violent content or calls for harm"]',
        source: FlagSource.REPORT,
    },
    c_t1_awaiting_moderator_note: {
        ...commentsThread1.c_t1_awaiting_moderator_note,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 5000000,
        objectTime: demoTimestamp - 9000000,
        violations: '["No violent content or calls for harm"]',
        source: FlagSource.REPORT,
    },
    c_t1_awaiting_report_multiple: {
        ...commentsThread1.c_t1_awaiting_report_multiple,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 5000000,
        objectTime: demoTimestamp - 9000000,
        violations: '["No violent content or calls for harm"]',
        source: FlagSource.REPORT,
    },
    // Approved
    c_t1_approved_human: {
        ...commentsThread1.c_t1_approved_human,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 20000000,
        objectTime: demoTimestamp - 20000000,
        violations: '["No violent content or calls for harm"]',
        source: FlagSource.PRE_MODERATION,
        judgement: JudgementType.APPROVE,
        humanJudgement: true,
        judgeKey: demoAuthors.a.key,
        judgementTime: demoTimestamp - 10000000,
        metadata: {
            explanation: 'This message can be interpreted as a call for harm.',
            rewrite: "Here's why I think X, Y, and Z are bad.",
            rulesBroken: '[2]',
        },
    },
    c_t1_approved_auto: {
        ...commentsThread1.c_t1_approved_auto,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 30000000,
        objectTime: demoTimestamp - 30000000,
        judgement: JudgementType.APPROVE,
        humanJudgement: false,
    },
    c_t1_approved_first_warning: {
        ...commentsThread1.c_t1_approved_first_warning,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 5000000,
        objectTime: demoTimestamp - 9000000,
        violations: '',
        source: FlagSource.REPORT,
    },
    c_t1_approved_first_warning_authorH: {
        ...commentsThread1.c_t1_approved_first_warning_authorH,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 5000000,
        objectTime: demoTimestamp - 9000000,
        violations: '',
        source: FlagSource.REPORT,
    },
    c_t1_approved_second_warning_authorH: {
        ...commentsThread1.c_t1_approved_second_warning_authorH,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 5000000,
        objectTime: demoTimestamp - 9000000,
        violations: '',
        source: FlagSource.REPORT,
    },
    c_t1_rejected: {
        ...commentsThread1.c_t1_rejected,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 40000000,
        objectTime: demoTimestamp - 40000000,
        appealText: 'This is just my opinion!',
        judgement: JudgementType.REJECT,
        humanJudgement: true,
        judgeKey: demoAuthors.a.key,
        judgementTime: demoTimestamp - 10000000,
        violations: '["No violent content or calls for harm"]',
        reasoning: '["No spam or commercial content"]',
        source: FlagSource.PRE_MODERATION,
        metadata: {
            explanation: 'This message can be interpreted as a call for harm.',
            rewrite: "Here's why I think X, Y, and Z are bad.",
            rulesBroken: '[2]',
        },
    },
    c_t1_rejected_blocked_first_warning: {
        ...commentsThread1.c_t1_rejected_blocked_first_warning,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 40000000,
        objectTime: demoTimestamp - 40000000,
        appealText: 'Are you going to warn me?',
        judgement: JudgementType.REJECT,
        humanJudgement: true,
        judgeKey: demoAuthors.a.key,
        judgementTime: demoTimestamp - 10000000,
        violations: '["No violent content or calls for harm"]',
        reasoning: '["No spam or commercial content"]',
        source: FlagSource.PRE_MODERATION,
        metadata: {
            explanation: 'This message can be interpreted as a call for harm.',
            rewrite: "Here's why I think X, Y, and Z are bad.",
            rulesBroken: '[2]',
        },
    },
    c_t1_rejected_blocked_second_warning: {
        ...commentsThread1.c_t1_rejected_blocked_second_warning,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 40000000,
        objectTime: demoTimestamp - 40000000,
        appealText: 'Do you want to warn me again?',
        judgement: JudgementType.REJECT,
        humanJudgement: true,
        judgeKey: demoAuthors.a.key,
        judgementTime: demoTimestamp - 10000000,
        violations: '["No violent content or calls for harm"]',
        reasoning: '["No spam or commercial content"]',
        source: FlagSource.PRE_MODERATION,
        metadata: {
            explanation: 'This message can be interpreted as a call for harm.',
            rewrite: "Here's why I think X, Y, and Z are bad.",
            rulesBroken: '[2]',
        },
    },
    c_t1_rejected_first_warning: {
        ...commentsThread1.c_t1_rejected_first_warning,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 40000000,
        objectTime: demoTimestamp - 40000000,
        appealText: 'Are you going to warn me?',
        judgement: JudgementType.REJECT,
        humanJudgement: true,
        judgeKey: demoAuthors.a.key,
        judgementTime: demoTimestamp - 10000000,
        violations: '["No violent content or calls for harm"]',
        reasoning: '["No spam or commercial content"]',
        source: FlagSource.PRE_MODERATION,
        metadata: {
            explanation: 'This message can be interpreted as a call for harm.',
            rewrite: "Here's why I think X, Y, and Z are bad.",
            rulesBroken: '[2]',
        },
    },
    c_t1_rejected_second_warning: {
        ...commentsThread1.c_t1_rejected_second_warning,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 40000000,
        objectTime: demoTimestamp - 40000000,
        appealText: 'Do you want to warn me again?',
        judgement: JudgementType.REJECT,
        humanJudgement: true,
        judgeKey: demoAuthors.a.key,
        judgementTime: demoTimestamp - 10000000,
        violations: '["No violent content or calls for harm"]',
        reasoning: '["No spam or commercial content"]',
        source: FlagSource.PRE_MODERATION,
        metadata: {
            explanation: 'This message can be interpreted as a call for harm.',
            rewrite: "Here's why I think X, Y, and Z are bad.",
            rulesBroken: '[2]',
        },
    },
    c_t1_rejected_deleted: {
        ...commentsThread1.c_t1_rejected,
        deleted: true,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 40000000,
        objectTime: demoTimestamp - 40000000,
        appealText: 'I will just delete my comment',
        judgement: JudgementType.REJECT,
        humanJudgement: true,
        judgeKey: demoAuthors.a.key,
        judgementTime: demoTimestamp - 10000000,
        violations: '["No violent content or calls for harm"]',
        reasoning: '["No spam or commercial content"]',
        source: FlagSource.PRE_MODERATION,
        metadata: {
            explanation: 'This message can be interpreted as a call for harm.',
            rewrite: "Here's why I think X, Y, and Z are bad.",
            rulesBroken: '[2]',
        },
    },
    c_t1_awaiting_deleted: {
        ...commentsThread1.c_t1_awaiting_ai,
        deleted: true,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        time: demoTimestamp - 40000000,
        objectTime: demoTimestamp - 40000000,
        appealText: 'I will just delete my comment',
        violations: '["No violent content or calls for harm"]',
        source: FlagSource.PRE_MODERATION,
        metadata: {
            explanation: 'This message can be interpreted as a call for harm.',
            rewrite: "Here's why I think X, Y, and Z are bad.",
            rulesBroken: '[2]',
        },
    },
    c_t1_awaiting_blocked: {
        ...commentsThread1.c_t1_awaiting_blocked,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread1.key,
        structureKey: 'question',
        objectTime: commentsThread1.c_t1_awaiting_blocked.time,
        appealText: "What's wrong with this?",
        violations: '["Avoid sarcasm and dismissive responses"]',
        source: FlagSource.PRE_MODERATION,
        metadata: {
            explanation: 'The message uses general snark and mockery. This is likely to lead to unproductive discourse.',
            rewrite: "Here's why I think X, Y, and Z are bad.",
            rulesBroken: '[5]',
        },
    },
};

const modTasksThread2 = {
    //Awaitng decision
    c_t2_awaiting_ai: {
        ...commentsThread2.c_t2_awaiting_ai,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread2.key,
        structureKey: 'question',
        time: demoTimestamp - 10000000,
        objectTime: demoTimestamp - 10000000,
        appealText: 'I think my comment is harmless.',
        violations: '["No violent content or calls for harm"]',
        source: FlagSource.PRE_MODERATION,
        metadata: {
            explanation: 'This message can be interpreted as a call for harm.',
            rewrite: "Here's why I think X, Y, and Z are bad.",
            rulesBroken: '[2]',
        },
    },
    c_t2_approved_human: {
        ...commentsThread2.c_t2_approved_human,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread2.key,
        structureKey: 'question',
        time: demoTimestamp - 20000000,
        objectTime: demoTimestamp - 20000000,
        violations: '["No violent content or calls for harm"]',
        source: FlagSource.PRE_MODERATION,
        judgement: JudgementType.APPROVE,
        humanJudgement: true,
        judgeKey: demoAuthors.a.key,
        judgementTime: demoTimestamp - 10000000,
        metadata: {
            explanation: 'This message can be interpreted as a call for harm.',
            rewrite: "Here's why I think X, Y, and Z are bad.",
            rulesBroken: '[2]',
        },
    },
    c_t2_approved_auto: {
        ...commentsThread2.c_t2_approved_auto,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread2.key,
        structureKey: 'question',
        time: demoTimestamp - 30000000,
        objectTime: demoTimestamp - 30000000,
        judgement: JudgementType.APPROVE,
        humanJudgement: false,
    },
    c_t2_rejected: {
        ...commentsThread2.c_t2_rejected,
        from: null,
        type: ModTaskType.COMMENT,
        instanceKey: modulePublicQuestion.thread2.key,
        structureKey: 'question',
        time: demoTimestamp - 40000000,
        objectTime: demoTimestamp - 40000000,
        appealText: 'This is just my opinion!',
        judgement: JudgementType.REJECT,
        humanJudgement: true,
        judgeKey: demoAuthors.a.key,
        judgementTime: demoTimestamp - 10000000,
        violations: '["No violent content or calls for harm"]',
        reasoning: '["No spam or commercial content"]',
        source: FlagSource.PRE_MODERATION,
        metadata: {
            explanation: 'This message can be interpreted as a call for harm.',
            rewrite: "Here's why I think X, Y, and Z are bad.",
            rulesBroken: '[2]',
        },
    },
};

const modTasksThread1SubQueues = {
    awaiting_decision: {
        c_t1_awaiting_ai: {
            key: commentsThread1.c_t1_awaiting_ai.key,
            time: commentsThread1.c_t1_awaiting_ai.time,
            type: ModTaskType.COMMENT,
        },
        c_t1_awaiting_blocklist: {
            key: commentsThread1.c_t1_awaiting_blocklist.key,
            time: commentsThread1.c_t1_awaiting_blocklist.time,
            type: ModTaskType.COMMENT,
        },
        c_t1_awaiting_report: {
            key: commentsThread1.c_t1_awaiting_report.key,
            time: commentsThread1.c_t1_awaiting_report.time,
            type: ModTaskType.COMMENT,
        },
        c_t1_awaiting_blocked: {
            key: commentsThread1.c_t1_awaiting_blocked.key,
            time: commentsThread1.c_t1_awaiting_blocked.time,
            type: ModTaskType.COMMENT,
        },
    },
    human_approved: {
        c_t1_approved_human: {
            key: commentsThread1.c_t1_approved_human.key,
            time: commentsThread1.c_t1_approved_human.time,
            type: ModTaskType.COMMENT,
        },
    },
    auto_approved: {
        c_t1_approved_auto: {
            key: commentsThread1.c_t1_approved_auto.key,
            time: commentsThread1.c_t1_approved_auto.time,
            type: ModTaskType.COMMENT,
        },
    },
    rejected: {
        c_t1_rejected: {
            key: commentsThread1.c_t1_rejected.key,
            time: commentsThread1.c_t1_rejected.time,
            type: ModTaskType.COMMENT,
        },
    },
};

const modTasksThread2SubQueues = {
    awaiting_decision: {
        c_t2_awaiting_ai: {
            key: commentsThread2.c_t2_awaiting_ai.key,
            time: commentsThread2.c_t2_awaiting_ai.time,
            type: ModTaskType.COMMENT,
        },
    },
    human_approved: {
        c_t2_approved_human: {
            key: commentsThread2.c_t2_approved_human.key,
            time: commentsThread2.c_t2_approved_human.time,
            type: ModTaskType.COMMENT,
        },
    },
    auto_approved: {
        c_t2_approved_auto: {
            key: commentsThread2.c_t2_approved_auto.key,
            time: commentsThread2.c_t2_approved_auto.time,
            type: ModTaskType.COMMENT,
        },
    },
    rejected: {
        c_t2_rejected: {
            key: commentsThread2.c_t2_rejected.key,
            time: commentsThread2.c_t2_rejected.time,
            type: ModTaskType.COMMENT,
        },
    },
};

const globalQueueCounts = {
    awaiting_decision:
        Object.keys(modTasksThread1SubQueues.awaiting_decision).length + Object.keys(modTasksThread2SubQueues.awaiting_decision).length,
    human_approved:
        Object.keys(modTasksThread1SubQueues.human_approved).length + Object.keys(modTasksThread2SubQueues.human_approved).length,
    auto_approved: Object.keys(modTasksThread1SubQueues.auto_approved).length + Object.keys(modTasksThread2SubQueues.auto_approved).length,
    rejected: Object.keys(modTasksThread1SubQueues.rejected).length + Object.keys(modTasksThread2SubQueues.rejected).length,
};

const modulePrivateModeratorNotes = {
    c_t1_awaiting_moderator_note: {
        c_t1_awaiting_moderator_note_n0: {
            from: demoAuthors.c.key,
            key: 'c_t1_awaiting_moderator_note_n0',
            modTaskKey: modTasksThread1.c_t1_awaiting_moderator_note.key,
            time: demoTimestamp - 1000,
            text: 'This could be spam',
        },
        c_t1_awaiting_moderator_note: {
            from: demoAuthors.d.key,
            key: 'c_t1_awaiting_moderator_note_n1',
            modTaskKey: modTasksThread1.c_t1_awaiting_moderator_note.key,
            time: demoTimestamp - 500,
            text: 'Yeah maybe, seems likely',
        },
    },
};

const modulePublicModeratorNotes = {
    moderatorNotes: {
        c_t1_rejected: {
            amount: 2,
        },
    },
};

const defaultReports = {
    [modTasksThread1.c_t1_awaiting_report.key]: {
        report_c_t1_awaiting_report_1: {
            key: 'report_c_t1_awaiting_report_1',
            author: demoModAuthors[modTasksThread1.c_t1_awaiting_report.key],
            reporter: demoAuthors.e,
            time: demoTimestamp,
            violations: ['Violation 1'],
        },
    },
    [modTasksThread1.c_t1_awaiting_report_multiple.key]: {
        report_c_t1_awaiting_report_multiple_1: {
            key: 'report_c_t1_awaiting_report_multiple_1',
            author: demoModAuthors[modTasksThread1.c_t1_awaiting_report_multiple.key],
            reporter: demoAuthors.e,
            time: demoTimestamp - 1000000,
            violations: ['Violation 1'],
        },
        report_c_t1_awaiting_report_multiple_2: {
            key: 'report_c_t1_awaiting_report_multiple_2',
            author: demoModAuthors[modTasksThread1.c_t1_awaiting_report_multiple.key],
            reporter: demoAuthors.d,
            time: demoTimestamp,
            violations: ['Violation 2'],
        },
    },
};

const moduleUserBookmarks = {
    [modTasksThread1.c_t1_awaiting_ai.key]: {
        instanceKey: modTasksThread1.c_t1_awaiting_ai.instanceKey,
        isBookmarked: true,
        key: modTasksThread1.c_t1_awaiting_ai.key,
        time: demoTimestamp - 9500000,
        type: ModTaskType.COMMENT,
    },
};

const moduleUserBookmarksCount = { bookmarkCount: 1 };

async function mockGetModeratorNotes({ modTaskKey }) {
    return modulePrivateModeratorNotes[modTaskKey];
}

async function mockSetModeratorNote({ from, text, modTaskKey }) {
    return {};
}

const defaultWatchStates: Record<string, TaskWatchState> = {
    c_t1_approved_auto: {
        modId: demoAuthors.e.key,
        sessionId: '123456789',
        time: Date.now(),
        name: demoAuthors.e.name,
        photoUrl: demoAuthors.e.photoUrl,
    },
};

const taskWatchStates: Record<string, TaskWatchState> = {
    c_t1_awaiting_ai: {
        modId: demoAuthors.a.key,
        sessionId: '23456789',
        time: Date.now(),
        name: demoAuthors.a.name,
        photoUrl: demoAuthors.a.photoUrl,
    },
    c_t1_awaiting_report: {
        modId: demoAuthors.b.key,
        sessionId: '3456789',
        time: Date.now(),
        name: demoAuthors.b.name,
        photoUrl: demoAuthors.b.photoUrl,
    },
};

const modulePublicTaskWatchState = {
    taskWatchState: taskWatchStates,
};

const modulePrivateSuspensionSuspensionsByUser: Record<string, Record<string, SuspensionObject>> = {
    [demoAuthors.c.key]: {
        user_c_suspension_warning_0: {
            key: 'user_c_suspension_0',
            time: demoTimestamp,
            suspensionType: SuspensionStatusType.WARNED,
            recipientKey: demoAuthors.c.key,

            structureKey: modTasksThread1.c_t1_rejected_first_warning.structureKey,
            instanceKey: modTasksThread1.c_t1_rejected_first_warning.instanceKey,
            objectKey: modTasksThread1.c_t1_rejected_first_warning.key,
            objectType: modTasksThread1.c_t1_rejected_first_warning.type,
        },
        user_c_suspension_warning_1: {
            key: 'user_c_suspension_1',
            time: demoTimestamp,
            suspensionType: SuspensionStatusType.WARNED,
            recipientKey: demoAuthors.c.key,

            structureKey: modTasksThread1.c_t1_rejected_second_warning.structureKey,
            instanceKey: modTasksThread1.c_t1_rejected_second_warning.instanceKey,
            objectKey: modTasksThread1.c_t1_rejected_second_warning.key,
            objectType: modTasksThread1.c_t1_rejected_second_warning.type,
        },
    },
    [demoAuthors.d.key]: {
        user_d_suspension_warning_0: {
            key: 'user_d_suspension_warning_0',
            time: demoTimestamp,
            suspensionType: SuspensionStatusType.WARNED,
            recipientKey: demoAuthors.d.key,

            structureKey: modTasksThread1.c_t1_rejected_blocked_first_warning.structureKey,
            instanceKey: modTasksThread1.c_t1_rejected_blocked_first_warning.instanceKey,
            objectKey: modTasksThread1.c_t1_rejected_blocked_first_warning.key,
            objectType: modTasksThread1.c_t1_rejected_blocked_first_warning.type,
        },
        user_d_suspension_warning_1: {
            key: 'user_d_suspension_warning_1',
            time: demoTimestamp,
            suspensionType: SuspensionStatusType.WARNED,
            recipientKey: demoAuthors.d.key,

            structureKey: modTasksThread1.c_t1_rejected_blocked_second_warning.structureKey,
            instanceKey: modTasksThread1.c_t1_rejected_blocked_second_warning.instanceKey,
            objectKey: modTasksThread1.c_t1_rejected_blocked_second_warning.key,
            objectType: modTasksThread1.c_t1_rejected_blocked_second_warning.type,
        },
        user_d_suspension_block: {
            key: 'user_d_suspension_block',
            time: demoTimestamp,
            suspensionType: SuspensionStatusType.BLOCKED,
            recipientKey: demoAuthors.d.key,
            from: 'a',
            blockedByName: 'Alice Adams',
        },
    },
    [demoAuthors.g.key]: {
        user_g_suspension_warning_0: {
            key: 'user_g_suspension_0',
            time: demoTimestamp,
            suspensionType: SuspensionStatusType.WARNED,
            recipientKey: demoAuthors.g.key,

            structureKey: modTasksThread1.c_t1_approved_first_warning.structureKey,
            instanceKey: modTasksThread1.c_t1_approved_first_warning.instanceKey,
            objectKey: modTasksThread1.c_t1_approved_first_warning.key,
            objectType: modTasksThread1.c_t1_approved_first_warning.type,
        },
    },
    [demoAuthors.h.key]: {
        user_h_suspension_warning_0: {
            key: 'user_h_suspension_warning_0',
            time: demoTimestamp,
            suspensionType: SuspensionStatusType.WARNED,
            recipientKey: demoAuthors.h.key,

            structureKey: modTasksThread1.c_t1_approved_first_warning_authorH.structureKey,
            instanceKey: modTasksThread1.c_t1_approved_first_warning_authorH.instanceKey,
            objectKey: modTasksThread1.c_t1_approved_first_warning_authorH.key,
            objectType: modTasksThread1.c_t1_approved_first_warning_authorH.type,
        },
        user_h_suspension_warning_1: {
            key: 'user_h_suspension_warning_1',
            time: demoTimestamp,
            suspensionType: SuspensionStatusType.WARNED,
            recipientKey: demoAuthors.h.key,

            structureKey: modTasksThread1.c_t1_approved_second_warning_authorH.structureKey,
            instanceKey: modTasksThread1.c_t1_approved_second_warning_authorH.instanceKey,
            objectKey: modTasksThread1.c_t1_approved_second_warning_authorH.key,
            objectType: modTasksThread1.c_t1_approved_second_warning_authorH.type,
        },
    },
};

const moduleUserRead = {
    [demoAuthors.c.key]: {
        global: {
            suspension: {
                ...modulePrivateSuspensionSuspensionsByUser[demoAuthors.c.key],
            },
        },
    },
    [demoAuthors.d.key]: {
        global: {
            suspension: {
                blockStatus: {
                    ...modulePrivateSuspensionSuspensionsByUser[demoAuthors.d.key].user_d_suspension_block,
                },
                suspensions: {
                    ...modulePrivateSuspensionSuspensionsByUser[demoAuthors.d.key],
                },
            },
        },
    },
    [demoAuthors.g.key]: {
        global: {
            suspension: {
                suspensions: {
                    ...modulePrivateSuspensionSuspensionsByUser[demoAuthors.g.key],
                },
            },
        },
    },
    [demoAuthors.h.key]: {
        global: {
            suspension: {
                suspensions: {
                    ...modulePrivateSuspensionSuspensionsByUser[demoAuthors.h.key],
                },
            },
        },
    },
};

const demoUserProfileData = {
    a: {
        preview: demoAuthors.a,
        commentCount: 0,
        flagsGivenCount: 10,
        flagsReceivedCount: 0,
        rejectedCommentCount: 0,
        isBlocked: false,
    },
    b: {
        preview: demoAuthors.b,
        commentCount: 3,
        flagsGivenCount: 0,
        flagsReceivedCount: 3,
        rejectedCommentCount: 0,
        isBlocked: true,
    },
    c: {
        preview: demoAuthors.c,
        commentCount: 2,
        flagsGivenCount: 0,
        flagsReceivedCount: 2,
        rejectedCommentCount: 2,
        suspensions: modulePrivateSuspensionSuspensionsByUser[demoAuthors.c.key],
    },
    d: {
        preview: demoAuthors.d,
        commentCount: 2,
        flagsGivenCount: 0,
        flagsReceivedCount: 2,
        rejectedCommentCount: 2,
        suspensions: modulePrivateSuspensionSuspensionsByUser[demoAuthors.d.key],
    },
    e: {
        preview: demoAuthors.d,
        commentCount: 2,
        flagsGivenCount: 0,
        flagsReceivedCount: 0,
        rejectedCommentCount: 0,
        suspension: modulePrivateSuspensionSuspensionsByUser[demoAuthors.e.key],
    },
    f: {
        preview: demoAuthors.f,
        commentCount: 2,
        flagsGivenCount: 0,
        flagsReceivedCount: 0,
        rejectedCommentCount: 0,
        suspension: modulePrivateSuspensionSuspensionsByUser[demoAuthors.f.key],
    },
    g: {
        preview: demoAuthors.g,
        commentCount: 2,
        flagsGivenCount: 0,
        flagsReceivedCount: 1,
        rejectedCommentCount: 1,
        suspension: modulePrivateSuspensionSuspensionsByUser[demoAuthors.g.key],
    },
    h: {
        preview: demoAuthors.h,
        commentCount: 3,
        flagsGivenCount: 0,
        flagsReceivedCount: 2,
        rejectedCommentCount: 2,
        suspension: modulePrivateSuspensionSuspensionsByUser[demoAuthors.h.key],
    },
};

const instanceData = {
    thread1: {
        globals: {
            name: modulePublicQuestion.thread1.question,
        },
        collections: {
            comment: Object.values(commentsThread1),
            queue_filtered_awaiting_decision: Object.values(modTasksThread1SubQueues.awaiting_decision),
            queue_filtered_human_approved: Object.values(modTasksThread1SubQueues.human_approved),
            queue_filtered_auto_approved: Object.values(modTasksThread1SubQueues.auto_approved),
            queue_filtered_rejected: Object.values(modTasksThread1SubQueues.rejected),
            persona: Object.values(demoAuthors),
            backlink_article: Object.values(backlinksThread1),
        },
    },
};

async function mockGetModItemAuthors() {
    return demoModAuthors;
}

async function mockGetPersonasByIds({ personaIds }) {
    const personas = {};

    personaIds.map((id) => {
        const persona = defaultPersonaList.find((persona) => persona.key === id);
        personas[id] = persona;
    });

    return personas;
}

async function mockGetRemoteObjectFromDatabase({ collectionType, objectKey, instanceKey }) {
    return instanceData[instanceKey].collections[collectionType].find((c) => c.key === objectKey);
}

async function mockGetUserProfileData({ userKey }) {
    return demoUserProfileData[userKey];
}

async function mockGetIsUserBlocked({ userKey }) {
    const latestBlock = moduleUserRead[userKey]?.global?.suspension?.blockStatus;
    return latestBlock?.suspensionType === SuspensionStatusType.BLOCKED;
}

async function mockGetReportsForModTask({ key }) {
    return defaultReports[key];
}

async function mockWarnUser() {
    return null;
}

async function mockSetJudgement() {
    return null;
}

async function mockWatchTask() {
    return null;
}

async function mockUnwatchTask() {
    return null;
}

async function mockCalculateHeated() {
    return null;
}

async function mockGetUserSuspensions({ userKey }) {
    return modulePrivateSuspensionSuspensionsByUser[userKey];
}

async function mockGetUserQueue({ key }) {
    return Object.keys(demoModAuthors)
        .filter((modAuthorKey) => {
            return demoModAuthors[modAuthorKey].authorKey === key;
        })
        .map((modAuthorKey) => {
            return { ...modTasksThread1, ...modTasksThread2 }[modAuthorKey];
        });
}

async function mockGetBlocklist() {
    return demoBlocklistEntries;
}

const threadQueueCounts = {
    [modulePublicQuestion.thread1.key]: {
        awaiting_decision_count: Object.values(modTasksThread1SubQueues.awaiting_decision).length,
    },
};

export const demoBlocklistEntries = [
    { term: 'Lorem Ipsum1', isSingularTerm: true, hasAlternativeSpellings: false },
    { term: 'Lorem Ipsum2', isSingularTerm: false, hasAlternativeSpellings: true },
];

export const modDashboardDemoData = {
    watchStates: defaultWatchStates,
    modulePublicQuestion,
    threadQueueCounts,
    commentsThread1,
    demoAuthors,
    demoModAuthors,
    modulePrivateModeratorNotes,
    moduleUserBookmarks,
    moduleUserBookmarksCount,
    modulePublicModeratorNotes,
    modulePublicTaskWatchState,
    instanceData: instanceData,
    demoUserProfiledata: demoUserProfileData,
    globalQueueCounts: globalQueueCounts,
    modTaskQueues: {
        thread1: modTasksThread1,
        thread2: modTasksThread2,
    },
    modTaskSubQueues: {
        thread1: modTasksThread1SubQueues,
        thread2: modTasksThread2SubQueues,
    },
    serverCalls: {
        moderation: {
            getModItemAuthors: mockGetModItemAuthors,
            setJudgement: mockSetJudgement,
        },
        moderationZdf: {
            getModItemAuthorsByIds: mockGetModItemAuthors,
            getRemoteObjectFromDatabase: mockGetRemoteObjectFromDatabase,
            getUserProfileData: mockGetUserProfileData,
            getModeratorNotes: mockGetModeratorNotes,
            setModeratorNote: mockSetModeratorNote,
            getReportsForModTask: mockGetReportsForModTask,
            warnUser: mockWarnUser,
            calculateHeated: mockCalculateHeated,
            watchTask: mockWatchTask,
            unwatchTask: mockUnwatchTask,
            getUserQueue: mockGetUserQueue,
            getPersonasByIds: mockGetPersonasByIds,
            getBlocklist: mockGetBlocklist,
            autoApprove: async () => {},
        },
        suspension: {
            getUserSuspensions: mockGetUserSuspensions,
            getIsUserBlocked: mockGetIsUserBlocked,
        },
    },
};
